// Generic renderer for every `type: "table"` calculation entry (columns +
// keyed rows), driven entirely by the column format hints - see
// calculations.py's MONTHLY_STATS_COLUMNS etc. and CalcColumn in
// reporting-hub/frontend/src/lib/types.ts. Ports reporting-hub's
// CalculationTable.tsx + CalcValueTooltip.tsx: same per-cell hover tooltip
// (formula + explain components) and highlighted-cell treatment (see CLAUDE.md
// "Cost Realization"), using PrimeReact's Tooltip component instead of a
// Radix tooltip.

import React from 'react';
import { Tooltip } from 'primereact/tooltip';
import { formatCalcValue, isNumericFormat, buildCellTooltip, isCellHighlighted } from './calcFormat';

const TALL_TABLE_ROW_THRESHOLD = 20;
const TOOLTIP_TARGET_CLASS = 'cr-calc-cell-tooltip';

const CalculationTable = ({ entry }) => {
  if (!entry || !entry.columns) return null;
  const isTall = (entry.rows || []).length > TALL_TABLE_ROW_THRESHOLD;

  return (
    <div className="cr-table-frame" style={{ maxHeight: isTall ? '480px' : undefined }}>
      <Tooltip target={`.${TOOLTIP_TARGET_CLASS}`} />
      <table className="cr-data-table">
        <thead>
          <tr>
            {entry.columns.map((col) => (
              <th
                key={col.id}
                style={{
                  position: isTall ? 'sticky' : undefined,
                  top: isTall ? 0 : undefined,
                  zIndex: isTall ? 1 : undefined,
                  textAlign: isNumericFormat(col.format) ? 'right' : 'left',
                }}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(entry.rows || []).map((row, idx) => (
            <tr key={idx}>
              {entry.columns.map((col) => {
                const highlighted = isCellHighlighted(col, row);
                const tooltip = buildCellTooltip(col, row);
                return (
                  <td
                    key={col.id}
                    className={tooltip ? TOOLTIP_TARGET_CLASS : undefined}
                    data-pr-tooltip={tooltip || undefined}
                    data-pr-position="top"
                    style={{
                      textAlign: isNumericFormat(col.format) ? 'right' : 'left',
                      cursor: tooltip ? 'help' : undefined,
                      textDecoration: tooltip ? 'underline dotted var(--app-text-muted)' : undefined,
                      textUnderlineOffset: tooltip ? '3px' : undefined,
                      borderLeft: highlighted ? '2px solid #f59e0b' : undefined,
                      background: highlighted ? 'var(--app-warning-soft)' : undefined,
                    }}
                  >
                    {formatCalcValue(row[col.id] ?? null, col.format)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default CalculationTable;
